
import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Campaign {
  id: string;
  name: string;
  subject: string;
  preview_text?: string;
  template_id?: string;
  content?: string;
  audience_segments: string[];
  excluded_segments: string[];
  selected_subscriber_ids?: string[];
  send_date?: string;
  timezone: string;
  send_rate?: number;
  track_opens: boolean;
  track_clicks: boolean;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  created_at: string;
  updated_at: string;
  sent_at?: string;
  created_by?: string;
}

interface CampaignInput {
  id?: string;
  name: string;
  subject: string;
  previewText?: string;
  templateId?: string;
  content?: string;
  audienceSegments: string[];
  excludedSegments: string[];
  selectedSubscriberIds?: string[];
  sendDate?: Date;
  timezone: string;
  sendRate?: number;
  trackOpens: boolean;
  trackClicks: boolean;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
}

// Mock campaigns for fallback
const mockCampaigns: Campaign[] = [
  {
    id: "1",
    name: "Welcome Campaign",
    subject: "Welcome to our Newsletter!",
    preview_text: "We're excited to have you join our community.",
    template_id: "1", // Refers to the "Welcome Email" template
    content: "<h1>Welcome!</h1><p>Thank you for subscribing to our newsletter. We're excited to share our latest updates with you.</p>",
    audience_segments: ["new"],
    excluded_segments: [],
    timezone: "UTC",
    track_opens: true,
    track_clicks: true,
    status: 'draft',
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: "2",
    name: "Monthly Newsletter - February",
    subject: "Your February Newsletter",
    preview_text: "See what's new this month.",
    template_id: "2", // Refers to the "Monthly Newsletter" template
    content: "<h1>Monthly Newsletter</h1><p>Hello {{subscriber_name}},</p><p>Here are the latest updates from our team for February.</p>",
    audience_segments: ["all"],
    excluded_segments: ["dormant"],
    timezone: "America/New_York",
    send_date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    track_opens: true,
    track_clicks: true,
    status: 'scheduled',
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
  }
];

export const useCampaigns = () => {
  const queryClient = useQueryClient();

  // Fetch campaigns from the database
  const fetchCampaigns = async (): Promise<Campaign[]> => {
    try {
      const { data, error } = await supabase
        .from('newsletter_campaigns')
        .select(`
          id,
          name,
          subject,
          preview_text,
          template_id,
          content,
          metadata,
          status,
          created_at,
          updated_at,
          sent_at,
          created_by
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map the database response to our Campaign interface
      return (data || []).map(campaign => {
        // Parse metadata if it's a string
        const metadata = typeof campaign.metadata === 'string' 
          ? JSON.parse(campaign.metadata as string) 
          : campaign.metadata || {};
          
        // Ensure status is one of the expected literals
        const status = validateStatus(campaign.status);
          
        return {
          id: campaign.id,
          name: campaign.name,
          subject: campaign.subject,
          preview_text: campaign.preview_text,
          template_id: campaign.template_id,
          content: campaign.content,
          // Extract segments from metadata or provide defaults
          audience_segments: metadata.audience_segments || [],
          excluded_segments: metadata.excluded_segments || [],
          selected_subscriber_ids: metadata.selected_subscriber_ids,
          send_date: metadata.send_date,
          timezone: metadata.timezone || "UTC",
          send_rate: metadata.send_rate,
          track_opens: metadata.track_opens !== undefined ? metadata.track_opens : true,
          track_clicks: metadata.track_clicks !== undefined ? metadata.track_clicks : true,
          status,
          created_at: campaign.created_at,
          updated_at: campaign.updated_at,
          sent_at: campaign.sent_at,
          created_by: campaign.created_by
        };
      });
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      // If in development, return mock data as fallback
      if (process.env.NODE_ENV === 'development') {
        return mockCampaigns;
      }
      throw error;
    }
  };

  // Helper function to validate status is one of the expected literals
  const validateStatus = (status: string): Campaign['status'] => {
    const validStatuses: Campaign['status'][] = ['draft', 'scheduled', 'sending', 'sent', 'failed'];
    return validStatuses.includes(status as Campaign['status']) 
      ? (status as Campaign['status']) 
      : 'draft';
  };

  // Create campaign
  const createCampaign = async (campaign: CampaignInput): Promise<Campaign> => {
    try {
      // Prepare metadata from campaign input
      const metadata = {
        audience_segments: campaign.audienceSegments,
        excluded_segments: campaign.excludedSegments,
        selected_subscriber_ids: campaign.selectedSubscriberIds,
        send_date: campaign.sendDate?.toISOString(),
        timezone: campaign.timezone,
        send_rate: campaign.sendRate,
        track_opens: campaign.trackOpens,
        track_clicks: campaign.trackClicks
      };

      const { data, error } = await supabase
        .from('newsletter_campaigns')
        .insert({
          name: campaign.name,
          subject: campaign.subject,
          preview_text: campaign.previewText,
          template_id: campaign.templateId,
          content: campaign.content,
          metadata,
          status: campaign.status,
        })
        .select()
        .single();

      if (error) throw error;

      // Map the returned data to our Campaign interface
      return {
        id: data.id,
        name: data.name,
        subject: data.subject,
        preview_text: data.preview_text,
        template_id: data.template_id,
        content: data.content,
        audience_segments: metadata.audience_segments,
        excluded_segments: metadata.excluded_segments,
        selected_subscriber_ids: metadata.selected_subscriber_ids,
        send_date: metadata.send_date,
        timezone: metadata.timezone,
        send_rate: metadata.send_rate,
        track_opens: metadata.track_opens,
        track_clicks: metadata.track_clicks,
        status: campaign.status,
        created_at: data.created_at,
        updated_at: data.updated_at,
        sent_at: data.sent_at,
        created_by: data.created_by
      };
    } catch (error) {
      console.error("Error creating campaign:", error);
      // If in development, simulate successful creation with mock data
      if (process.env.NODE_ENV === 'development') {
        const newCampaign: Campaign = {
          id: Math.random().toString(36).substr(2, 9),
          name: campaign.name,
          subject: campaign.subject,
          preview_text: campaign.previewText,
          template_id: campaign.templateId,
          content: campaign.content,
          audience_segments: campaign.audienceSegments,
          excluded_segments: campaign.excludedSegments,
          selected_subscriber_ids: campaign.selectedSubscriberIds,
          send_date: campaign.sendDate?.toISOString(),
          timezone: campaign.timezone,
          send_rate: campaign.sendRate,
          track_opens: campaign.trackOpens,
          track_clicks: campaign.trackClicks,
          status: campaign.status,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        return newCampaign;
      }
      throw error;
    }
  };

  // Update campaign
  const updateCampaign = async (campaign: CampaignInput): Promise<Campaign> => {
    try {
      if (!campaign.id) {
        throw new Error("Campaign ID is required for updates");
      }

      // Prepare metadata from campaign input
      const metadata = {
        audience_segments: campaign.audienceSegments,
        excluded_segments: campaign.excludedSegments,
        selected_subscriber_ids: campaign.selectedSubscriberIds,
        send_date: campaign.sendDate?.toISOString(),
        timezone: campaign.timezone,
        send_rate: campaign.sendRate,
        track_opens: campaign.trackOpens,
        track_clicks: campaign.trackClicks
      };

      const { data, error } = await supabase
        .from('newsletter_campaigns')
        .update({
          name: campaign.name,
          subject: campaign.subject,
          preview_text: campaign.previewText,
          template_id: campaign.templateId,
          content: campaign.content,
          metadata,
          status: campaign.status,
        })
        .eq('id', campaign.id)
        .select()
        .single();

      if (error) throw error;

      // Map the returned data to our Campaign interface
      return {
        id: data.id,
        name: data.name,
        subject: data.subject,
        preview_text: data.preview_text,
        template_id: data.template_id,
        content: data.content,
        audience_segments: metadata.audience_segments,
        excluded_segments: metadata.excluded_segments,
        selected_subscriber_ids: metadata.selected_subscriber_ids,
        send_date: metadata.send_date,
        timezone: metadata.timezone,
        send_rate: metadata.send_rate,
        track_opens: metadata.track_opens,
        track_clicks: metadata.track_clicks,
        status: campaign.status,
        created_at: data.created_at,
        updated_at: data.updated_at,
        sent_at: data.sent_at,
        created_by: data.created_by
      };
    } catch (error) {
      console.error("Error updating campaign:", error);
      // If in development, simulate successful update
      if (process.env.NODE_ENV === 'development') {
        const mockCampaign = mockCampaigns.find(c => c.id === campaign.id);
        if (!mockCampaign) {
          throw new Error("Campaign not found");
        }
        
        const updatedCampaign: Campaign = {
          ...mockCampaign,
          name: campaign.name,
          subject: campaign.subject,
          preview_text: campaign.previewText,
          template_id: campaign.templateId,
          content: campaign.content,
          audience_segments: campaign.audienceSegments,
          excluded_segments: campaign.excludedSegments,
          selected_subscriber_ids: campaign.selectedSubscriberIds,
          send_date: campaign.sendDate?.toISOString(),
          timezone: campaign.timezone,
          send_rate: campaign.sendRate,
          track_opens: campaign.trackOpens,
          track_clicks: campaign.trackClicks,
          status: campaign.status,
          updated_at: new Date().toISOString()
        };
        return updatedCampaign;
      }
      throw error;
    }
  };

  // Delete campaign
  const deleteCampaign = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('newsletter_campaigns')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error("Error deleting campaign:", error);
      throw error;
    }
  };

  // Set up React Query hooks
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: fetchCampaigns,
  });

  // Create campaign mutation
  const { mutateAsync: createCampaignMutation } = useMutation({
    mutationFn: createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });

  // Update campaign mutation
  const { mutateAsync: updateCampaignMutation } = useMutation({
    mutationFn: updateCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });

  // Delete campaign mutation
  const { mutateAsync: deleteCampaignMutation } = useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });

  // Helper function to save (create or update) a campaign
  const saveCampaign = async (campaign: CampaignInput) => {
    try {
      if (campaign.id) {
        await updateCampaignMutation(campaign);
        toast.success("Campaign updated successfully");
        return campaign.id;
      } else {
        const newCampaign = await createCampaignMutation(campaign);
        toast.success("Campaign created successfully");
        return newCampaign.id;
      }
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast.error("Failed to save campaign");
      throw error;
    }
  };

  // Helper function to delete a campaign
  const removeCampaign = async (id: string) => {
    try {
      await deleteCampaignMutation(id);
      toast.success("Campaign deleted successfully");
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Failed to delete campaign");
      throw error;
    }
  };

  // Get draft campaigns
  const draftCampaigns = campaigns.filter(c => c.status === 'draft');
  
  // Get scheduled campaigns
  const scheduledCampaigns = campaigns.filter(c => c.status === 'scheduled');
  
  // Get sent campaigns
  const sentCampaigns = campaigns.filter(c => ['sent', 'sending'].includes(c.status));

  return {
    campaigns,
    draftCampaigns,
    scheduledCampaigns,
    sentCampaigns,
    isLoading,
    saveCampaign,
    deleteCampaign: removeCampaign,
  };
};
